import { useState } from 'react';
import { VotingWidget } from './VotingWidget';
import { ProposalMeta } from './ProposalMeta';
import { ProposalHistory } from './ProposalHistory';
import { VoteAuditLog } from './VoteAuditLog';
import { RelatedProposals } from './RelatedProposals';
import { AttachmentsSection } from './AttachmentsSection';
import { ProposalTags } from './ProposalTags';

/**
 * Proposal Modal Component
 * Displays full proposal details in a modal overlay with history tab
 */
export function ProposalModal({ proposal, onClose, onVote, voting, votingPower, showVoting }) {
  const [activeTab, setActiveTab] = useState('details');

  // Parse metadata
  const metadata = proposal.metadata ? (typeof proposal.metadata === 'string' ? JSON.parse(proposal.metadata) : proposal.metadata) : {};
  const tags = metadata.tags || [];
  const attachments = metadata.attachments || [];
  const relatedProposalIds = metadata.related_proposals || [];

  // Check if user is admin (for audit log visibility)
  const isAdmin = window.t24Governance?.canCreate || false;
  // Parse vote counts
  const votesFor = parseInt(proposal.votes_for || 0);
  const votesAgainst = parseInt(proposal.votes_against || 0);
  const votesAbstain = parseInt(proposal.votes_abstain || 0);
  const quorum = parseInt(proposal.quorum_required || 10);

  // Handle overlay click (close modal)
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      className="t24-proposal-modal-overlay"
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="t24-proposal-modal">
        {/* Modal Header */}
        <div className="t24-proposal-modal-header">
          <button className="t24-proposal-modal-close" onClick={onClose}>
            ✕
          </button>

          <div className="t24-proposal-card-header">
            <span className={`t24-proposal-category-badge ${proposal.category}`}>
              {window.t24Governance.categories[proposal.category] || proposal.category}
            </span>
            {proposal.priority && (
              <span className={`t24-proposal-priority-badge ${proposal.priority}`}>
                {proposal.priority}
              </span>
            )}
          </div>

          <h2 className="t24-proposal-title">{proposal.title}</h2>

          <ProposalMeta
            author={proposal.created_by}
            createdAt={proposal.created_at}
            endsAt={proposal.ends_at}
            status={proposal.status}
          />
        </div>

        {/* Tab Navigation */}
        <div className="t24-proposal-modal-tabs">
          <button
            className={`t24-proposal-tab ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            📋 Details
          </button>
          <button
            className={`t24-proposal-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            📜 History
          </button>
          {isAdmin && (
            <button
              className={`t24-proposal-tab ${activeTab === 'audit' ? 'active' : ''}`}
              onClick={() => setActiveTab('audit')}
            >
              🔍 Audit Log
            </button>
          )}
        </div>

        {/* Modal Body */}
        <div className="t24-proposal-modal-body">
          {/* Details Tab */}
          {activeTab === 'details' && (
            <>
              <div className="t24-proposal-modal-full-description">
                <p>{proposal.description}</p>
              </div>

              {/* Tags */}
              {tags.length > 0 && (
                <div className="t24-proposal-modal-section">
                  <h3>Tags</h3>
                  <ProposalTags tags={tags} />
                </div>
              )}

              {/* Attachments */}
              {attachments.length > 0 && (
                <div className="t24-proposal-modal-section">
                  <AttachmentsSection attachments={attachments} />
                </div>
              )}

              {/* Related Proposals */}
              {relatedProposalIds.length > 0 && (
                <div className="t24-proposal-modal-section">
                  <RelatedProposals
                    relatedProposalIds={relatedProposalIds}
                    currentProposalId={proposal.id}
                    onProposalClick={onClose}
                  />
                </div>
              )}

              {/* Voting Section */}
              {showVoting && proposal.status === 'active' && (
                <div className="t24-proposal-modal-voting">
                  <h3>Cast Your Vote</h3>

                  {votingPower.votingPower && (
                    <div className="t24-voting-power-indicator">
                      <span className="t24-voting-power-icon">⚡</span>
                      <div className="t24-voting-power-text">
                        <span>Your {window.t24Governance.strings.votingPower}:</span>
                        <span className="t24-voting-power-value">
                          {votingPower.votingPower.total_weight.toFixed(1)}
                        </span>
                      </div>
                    </div>
                  )}

                  <VotingWidget
                    proposalId={proposal.id}
                    votesFor={votesFor}
                    votesAgainst={votesAgainst}
                    votesAbstain={votesAbstain}
                    quorum={quorum}
                    onVote={onVote}
                    voting={voting}
                    userVote={voting.userVotes[proposal.id]}
                  />
                </div>
              )}
            </>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="t24-proposal-modal-history-tab">
              <ProposalHistory proposalId={proposal.id} />
            </div>
          )}

          {/* Audit Log Tab (Admin Only) */}
          {activeTab === 'audit' && isAdmin && (
            <div className="t24-proposal-modal-audit-tab">
              <VoteAuditLog proposalId={proposal.id} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
